import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "context/AuthContext";
import { get } from "lib/api";
import { isPortalRole } from "lib/portalRoles";
import { getStaffFrontendUrl, isStaffRole } from "lib/staffFrontend";

function StaffFrontendRedirect() {
  useEffect(() => {
    window.location.replace(getStaffFrontendUrl("/"));
  }, []);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <MDTypography variant="button">Opening staff ERP…</MDTypography>
        </Box>
      </Card>
    </Container>
  );
}

function extractCompanyKeys(profile) {
  const rel = profile?.companies;
  const rows = Array.isArray(rel?.data) ? rel.data : Array.isArray(rel) ? rel : [];
  return rows
    .map((c) => c?.attributes?.key ?? c?.key)
    .filter(Boolean)
    .map((k) => String(k).toLowerCase());
}

export default function PostLogin() {
  const { token, pluginRole, profile, activeCompany, setActiveCompany, authLoading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isMobile = location.pathname === "/m/post-login" || location.pathname.startsWith("/m/");
  const role = String(pluginRole || "").toLowerCase();

  const allowedKeysFromProfile = useMemo(() => extractCompanyKeys(profile), [profile]);
  const [allowedKeysFromApi, setAllowedKeysFromApi] = useState(null);
  const [apiMeta, setApiMeta] = useState({ profileExists: null, defaultCompanyKey: null });
  const [membershipsReady, setMembershipsReady] = useState(false);
  const allowedKeys = useMemo(() => {
    return Array.isArray(allowedKeysFromApi) && allowedKeysFromApi.length ? allowedKeysFromApi : allowedKeysFromProfile;
  }, [allowedKeysFromApi, allowedKeysFromProfile]);
  const hasMultiple = allowedKeys.length > 1;

  const [didAutoPick, setDidAutoPick] = useState(false);

  const staffDefaultPath = useMemo(() => {
    if (isMobile) {
      if (role === "authenticated") return "/m/auth-dashboard";
      return "/m/dashboard";
    }
    if (role === "authenticated") return "/auth-dashboard";
    return "/dashboard";
  }, [isMobile, role]);

  useEffect(() => {
    if (!token || authLoading || isPortalRole(role)) return;
    // Staff company selection lives in staff-erp; skip public-web membership prompts.
    if (isStaffRole(role)) {
      setMembershipsReady(true);
      return;
    }
    // Fetch memberships server-side so this works even if /api/user-profiles isn't accessible to the role.
    (async () => {
      try {
        const res = await get("/api/storefront/staff/companies");
        const companies = Array.isArray(res?.companies) ? res.companies.map((k) => String(k).toLowerCase()) : [];
        setAllowedKeysFromApi(companies);
        setApiMeta({
          profileExists: typeof res?.profileExists === "boolean" ? res.profileExists : null,
          defaultCompanyKey: res?.defaultCompanyKey ? String(res.defaultCompanyKey).toLowerCase() : null,
        });
        setMembershipsReady(true);
      } catch {
        // fallback to profile-derived membership
        setMembershipsReady(true);
      }
    })();
  }, [authLoading, isMobile, role, token]);

  const nextPath = useMemo(() => {
    try {
      const stored = sessionStorage.getItem("postLoginNext");
      if (typeof stored !== "string" || !stored) return null;
      const isValveBodyRegister = stored.startsWith("/register/valve-body/");
      if (isMobile && !stored.startsWith("/m") && !isValveBodyRegister) return null;
      if (!isMobile && stored.startsWith("/m")) return null;
      if (stored.includes("/authentication/")) return null;
      if (stored.endsWith("/sign-out")) return null;
      return stored;
    } catch {
      return null;
    }
  }, [isMobile]);

  const continueTo = nextPath || staffDefaultPath;

  // Match iOS: use the server default company first, then single-company auto-pick.
  useEffect(() => {
    if (!token || authLoading || isPortalRole(role)) return;
    if (isStaffRole(role)) return;
    if (!membershipsReady) return;
    if (didAutoPick) return;
    if (activeCompany) return;
    const defaultKey = String(apiMeta.defaultCompanyKey || "").toLowerCase();
    const nextCompany =
      defaultKey && allowedKeys.includes(defaultKey)
        ? defaultKey
        : allowedKeys.length === 1
        ? allowedKeys[0]
        : null;
    if (nextCompany) {
      setDidAutoPick(true);
      setActiveCompany(nextCompany);
      try {
        sessionStorage.removeItem("postLoginNext");
      } catch {
        // ignore
      }
      navigate(continueTo, { replace: true });
    }
  }, [
    activeCompany,
    allowedKeys,
    apiMeta.defaultCompanyKey,
    authLoading,
    continueTo,
    didAutoPick,
    isMobile,
    membershipsReady,
    navigate,
    role,
    setActiveCompany,
    token,
  ]);

  // must be logged in
  if (!token) {
    return <Navigate to={isMobile ? "/m/authentication/sign-in" : "/authentication/sign-in"} replace />;
  }

  // avoid flicker while /users/me is hydrating
  if (authLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Card variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <MDTypography variant="button">Loading session…</MDTypography>
          </Box>
        </Card>
      </Container>
    );
  }

  if (isPortalRole(role)) {
    const portalDefault = isMobile ? "/m/account" : "/account";
    return <Navigate to={nextPath || portalDefault} replace />;
  }

  // Staff (desktop + mobile) always land in staff-erp, never legacy /m/* staff UI.
  if (isStaffRole(role)) {
    try {
      sessionStorage.removeItem("postLoginNext");
    } catch {
      // ignore
    }
    return <StaffFrontendRedirect />;
  }

  // IMPORTANT: wait for staff memberships before redirecting.
  // Otherwise the public storefront company can cause an immediate redirect before
  // we learn the user's actual company access and should prompt for a tenant.
  if (!membershipsReady) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Card variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <MDTypography variant="button">Loading companies…</MDTypography>
          </Box>
        </Card>
      </Container>
    );
  }

  // If we already have a valid company selected, continue.
  if (activeCompany && (!allowedKeys.length || allowedKeys.includes(String(activeCompany).toLowerCase()))) {
    try {
      sessionStorage.removeItem("postLoginNext");
    } catch {
      // ignore
    }
    return <Navigate to={continueTo} replace />;
  }

  // If they belong to multiple companies and no default/current company applies, prompt.
  if (hasMultiple) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Card variant="outlined" sx={{ p: 3 }}>
          <MDTypography variant="h4" fontWeight={900} gutterBottom>
            Select company
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
            You have access to multiple companies. Choose which one to work in for this session.
          </MDTypography>

          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {allowedKeys.map((k) => (
              <MDButton
                key={k}
                variant={String(activeCompany || "").toLowerCase() === k ? "contained" : "outlined"}
                color="info"
                onClick={() => {
                  try {
                    sessionStorage.removeItem("postLoginNext");
                  } catch {
                    // ignore
                  }
                  setActiveCompany(k);
                  navigate(continueTo, { replace: true });
                }}
              >
                {k.toUpperCase()}
              </MDButton>
            ))}
          </Box>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card variant="outlined" sx={{ p: 3 }}>
        <MDTypography variant="h4" fontWeight={900} gutterBottom>
          Select company
        </MDTypography>
        <MDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
          This is only shown on non-production hosts when you have access to multiple companies.
        </MDTypography>

        {!hasMultiple ? (
          <Alert severity={apiMeta.profileExists === false ? "error" : "warning"} sx={{ mt: 2 }}>
            {apiMeta.profileExists === false
              ? "This user is missing a user-profile record. Ask an admin to create a user-profile and assign company memberships."
              : "No company selection available for this user. Ask an admin to assign company memberships on the user-profile."}
          </Alert>
        ) : null}

        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {allowedKeys.map((k) => (
            <MDButton
              key={k}
              variant="outlined"
              color="info"
              onClick={() => {
                setActiveCompany(k);
                navigate(staffDefaultPath, { replace: true });
              }}
            >
              {k.toUpperCase()}
            </MDButton>
          ))}
        </Box>

        {!allowedKeys.length ? (
          <Box sx={{ mt: 2 }}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => {
                try {
                  sessionStorage.removeItem("postLoginNext");
                } catch {
                  // ignore
                }
                logout();
                navigate(isMobile ? "/m/authentication/sign-in" : "/authentication/sign-in", { replace: true });
              }}
            >
              Sign out
            </MDButton>
          </Box>
        ) : null}
      </Card>
    </Container>
  );
}
